import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import { useSeo } from '@/hooks/useSeo';
import { getNewsById, relatedNews } from '@/utils/media';

export default function NewsDetailPage() {
  const { id = '' } = useParams();
  const article = getNewsById(id);

  useSeo({
    title: article ? `${article.title} - 91爱爱影视资讯` : '资讯内容不存在 - 91爱爱',
    description: article
      ? article.summary.slice(0, 80)
      : '未找到对应的影视资讯内容',
    keywords: article ? `91爱爱,影视资讯,${article.category},${article.title}` : '91爱爱,影视资讯',
  });

  if (!article) {
    return (
      <SiteLayout>
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 md:px-6">
          <i className="ri-file-unknow-line text-6xl text-foreground-300" />
          <h1 className="mt-5 font-heading text-xl font-bold text-foreground-900">资讯内容不存在</h1>
          <p className="mt-2 text-sm text-foreground-500">该资讯可能已被移除，或链接有误。</p>
          <Link
            to="/news"
            className="mt-6 flex h-10 items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
          >
            <i className="ri-arrow-left-s-line" />
            返回资讯列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const related = relatedNews(article, 3);

  return (
    <SiteLayout>
      <div className="mx-auto max-w-[1280px] px-4 pt-6 md:px-6">
        <Breadcrumb items={[{ label: '影视资讯', to: '/news' }, { label: article.category, to: '/news' }, { label: '正文' }]} />
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6">
        <div className="grid gap-10 lg:grid-cols-[1fr_300px]">
          <article className="min-w-0">
            <span className="inline-flex items-center gap-1.5 rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-800">
              <i className="ri-price-tag-3-line" />
              {article.category}
            </span>
            <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
              {article.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 border-b border-background-200 pb-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-newspaper-line" />
                来源：{article.source}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                发布时间：{article.publishedAt}
              </span>
            </div>

            <div className="relative mt-6 aspect-[16/9] w-full overflow-hidden rounded-lg bg-background-200">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 91爱爱影视资讯`}
                className="absolute inset-0 h-full w-full object-cover object-top"
              />
            </div>

            <div className="mt-6 flex flex-col gap-5">
              {article.content.map((paragraph, index) => (
                <p key={index} className="text-sm leading-7 text-foreground-700 md:text-base md:leading-8">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-background-200 bg-background-100/60 p-4">
              <p className="text-xs leading-relaxed text-foreground-500">
                <i className="ri-information-line mr-1 text-primary-600" />
                本文内容为演示示例，不代表任何真实报道。
              </p>
              <Link
                to="/news"
                className="flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 transition-colors hover:text-accent-600 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line" />
                返回资讯列表
              </Link>
            </div>
          </article>

          <aside className="min-w-0">
            <div className="sticky top-24 flex flex-col gap-5">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-links-line text-primary-600" />
                  相关文章推荐
                </h2>
                <div className="flex flex-col gap-1">
                  {related.map((item) => (
                    <NewsCard key={item.id} article={item} variant="row" />
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
                <h2 className="mb-2 font-heading text-base font-bold text-primary-800">热门影视资源</h2>
                <p className="text-xs leading-relaxed text-primary-900/80">
                  想找点片子看？前往影视分类页，按类型、地区与年份快速筛选。
                </p>
                <Link
                  to="/category/hot"
                  className="mt-3 inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-600 px-4 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
                >
                  <i className="ri-fire-line" />
                  浏览热门推荐
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}